import moment from 'moment-timezone';
import { NEXT_DAY_SUFFIX } from '../constants/time';

export type FormattedTimeRange = {
  start: string;
  end: string;
  timeSlot: string;
};

export type TimeFormatOptions = {
  nextDayIndicator?: string;
  format?: string;
  isNextDaySlot?: boolean;
};

/**
 * Parses a time string and handles "(Next Day)" suffix
 * @param timeString - Time string like "2:00 AM" or "2:00 AM (Next Day)"
 * @param baseDate - Base date in MM/DD/YYYY or YYYYMMDD format
 * @param timezone - Timezone string
 * @returns Moment object with correct date
 */
export const parseTimeWithNextDay = (timeString: string, baseDate: string, timezone: string): moment.Moment | null => {
  if (!timeString) {
    return null;
  }

  const actualTime = stripNextDaySuffix(timeString).trim();
  const isNextDay = hasNextDaySuffix(timeString);

  // Detect date format and construct appropriate moment format string
  let dateTimeFormat: string;
  let dateTimeString: string;

  if (baseDate.includes('/')) {
    // MM/DD/YYYY format
    dateTimeFormat = 'h:mm A MM/DD/YYYY';
    dateTimeString = `${actualTime} ${baseDate}`;
  } else {
    // YYYYMMDD format
    dateTimeFormat = `YYYYMMDD h:mm A`;
    dateTimeString = `${baseDate} ${actualTime}`;
  }

  let timeMoment = moment.tz(dateTimeString, dateTimeFormat, timezone);

  if (isNextDay) {
    timeMoment = timeMoment.add(1, 'day');
  }

  return timeMoment;
};

/**
 * Parses different input types into moment objects
 * @param start - Start time input
 * @param end - End time input
 * @returns Parsed moment objects or null if invalid
 */
const parseTimeInputs = (
  start: moment.Moment | string,
  end: moment.Moment | string
): { startMoment: moment.Moment; endMoment: moment.Moment } => {
  if (typeof start === 'string' && typeof end === 'string') {
    return {
      startMoment: moment(start, 'HH:mm'),
      endMoment: moment(end, 'HH:mm'),
    };
  }

  return {
    startMoment: start as moment.Moment,
    endMoment: end as moment.Moment,
  };
};

/**
 * Adjusts end time if it's chronologically before start time (next-day slot)
 * @param startMoment - Start time moment
 * @param endMoment - End time moment
 * @returns Adjusted end time moment
 */
export const adjustNextDayTime = (startMoment: moment.Moment, endMoment: moment.Moment): moment.Moment => {
  if (endMoment.isBefore(startMoment)) {
    return endMoment.clone().add(1, 'day');
  }

  return endMoment;
};

/**
 * Formats a time range from various input types, indicating when end time is next day
 * @param start - Start time as moment object or time string
 * @param end - End time as moment object or time string
 * @param options - Formatting options
 * @returns Formatted time range
 */
export const formatTimeRange = (
  start: moment.Moment | string,
  end: moment.Moment | string,
  options: TimeFormatOptions = {}
): FormattedTimeRange => {
  const { nextDayIndicator = NEXT_DAY_SUFFIX, format = 'h:mma', isNextDaySlot = false } = options;

  // Parse inputs into moment objects
  const { startMoment, endMoment: initialEndMoment } = parseTimeInputs(start, end);
  let endMoment = initialEndMoment;

  // Handle next-day slots
  endMoment = adjustNextDayTime(startMoment, endMoment);

  const isNextDay = (!endMoment.isSame(startMoment, 'day') || isNextDaySlot) && endMoment.format('HH:mm') !== '00:00';

  const formattedStart = startMoment.format(format);
  const formattedEnd = isNextDay ? `${endMoment.format(format)} ${String(nextDayIndicator)}` : endMoment.format(format);

  return {
    start: formattedStart,
    end: formattedEnd,
    timeSlot: `${formattedStart} - ${formattedEnd}`,
  };
};

/**
 * Creates a time slot with proper next-day handling
 * @param startTime - Start time in HH:mm format
 * @param endTime - End time in HH:mm format
 * @param date - Date in MM/DD/YYYY format
 * @param timezone - Timezone string
 * @returns Time slot with ISO strings
 */
export const createTimeSlot = (
  startTime: string,
  endTime: string,
  date: string,
  timezone: string
): { startTimeISO: string; endTimeISO: string } | null => {
  if (!startTime || !endTime || !date || !timezone) {
    return null;
  }

  const startMoment = moment.tz(`${startTime} ${date}`, 'HH:mm MM/DD/YYYY', timezone);
  let endMoment = moment.tz(`${endTime} ${date}`, 'HH:mm MM/DD/YYYY', timezone);

  // Adjust for next-day slots
  endMoment = adjustNextDayTime(startMoment, endMoment);

  return {
    startTimeISO: startMoment.toISOString(),
    endTimeISO: endMoment.toISOString(),
  };
};

/**
 * Strips "(Next Day)" suffix from time string
 * @param timeString - Time string that may contain suffix
 * @returns Clean time string
 */
export const stripNextDaySuffix = (timeString: string): string => {
  if (!timeString) {
    return timeString;
  }
  return timeString.replace(NEXT_DAY_SUFFIX, '').trim();
};

/**
 * Checks if a time string has "(Next Day)" suffix
 * @param timeString - Time string to check
 * @returns True if it has next day suffix
 */
export const hasNextDaySuffix = (timeString: string): boolean => timeString.includes(NEXT_DAY_SUFFIX);
